import { useEffect, useMemo, useState } from 'react'
import { Prompt, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Button, ConfirmDialog, Notification, PageView, PageTopBar } from '../../../../components'
import { Loader } from '../../../../components/Loader'
import { fetchWarehouse } from '../../../../services/warehouseSevices'
import {
  fetchReceivingSettings,
  fetchReceivingSuggestions,
  updateReceivingSettings,
  fetchReceivingHistory,
} from '../../../../services/inboundAppointmentServices'
import { toPutBody, parseReceivingError, canRevert, buildRevertBody } from '../../../../utils/receivingConfig'
import { ReceivingTabs } from '../ReceivingTabs'
import { ReceivingHeader } from '../ReceivingHeader'
import { SettingsCard, validateSettings } from './SettingsCard'
import { FranjasCard } from './FranjasCard'
import { FranjaFormModal } from './FranjaFormModal'
import { SaveReasonModal } from './SaveReasonModal'
import { ConflictsDialog } from './ConflictsDialog'
import { PostSaveWarningsBanner } from './PostSaveWarningsBanner'
import { FIVE_MINUTES_MS } from '../../../../helpers/cacheTimes'

const FIRST_SAVE_KEYS = ['dock_count', 'units_per_door_franja', 'daily_processing_man_hours']
// Codes with their own surface: the flat 400 stays in the reason modal, and these
// two open dedicated dialogs. Everything else must reach the user through the
// alert below — an allow-list here would silently swallow any unenumerated code
// (403/404/429, or a new 422 added backend-side).
const CODES_HANDLED_ELSEWHERE = ['invalid_param', 'franja_has_booked_appointments', 'concurrent_update']

const seedDraft = data => ({ settings: { ...(data?.settings || {}) }, franjas: (data?.franjas || []).map(f => ({ ...f })) })
const isDirtyDraft = (seed, draft) => Object.keys(toPutBody(seed, draft, 'x')).length > 1
// Identity for a draft franja: its server id once saved, its client-only _tempKey until then.
const franjaKey = f => f.id ?? f._tempKey

export const ReceivingSettingsContainer = () => {
  const { warehouse_id } = useParams()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { data: warehouse } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    enabled: !!warehouse_id,
  })
  // refetchOnWindowFocus stays off here: this screen is explicitly designed for two concurrent
  // editors (see the concurrent_update dialog below), so a focus refetch mid-edit would silently
  // replace an in-progress draft with a colleague's save via the [seed] effect, with Prompt
  // disarmed (isDirty would already read false) and no warning shown.
  const settingsQuery = useQuery(['receiving_settings', warehouse_id], () => fetchReceivingSettings(warehouse_id), {
    refetchOnWindowFocus: false,
    enabled: !!warehouse_id,
  })
  const { data: suggestions } = useQuery(['receiving_suggestions', warehouse_id], () => fetchReceivingSuggestions(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    enabled: !!warehouse_id,
  })

  const seed = settingsQuery.data
  const [draft, setDraft] = useState(null)
  useEffect(() => {
    if (seed) setDraft(seedDraft(seed))
  }, [seed])

  const [franjaModal, setFranjaModal] = useState({ open: false, initial: null })
  const [reasonOpen, setReasonOpen] = useState(false)
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })
  const [saveError, setSaveError] = useState(null) // parsed error, rendered by Task 10
  const [lastSaveResult, setLastSaveResult] = useState(null) // { warnings, historyRowId }, rendered by Task 11
  const [historyRow, setHistoryRow] = useState(null) // full row fetched below, needed by canRevert/buildRevertBody
  const [revertOpen, setRevertOpen] = useState(false)

  const firstSave = seed ? !seed.provisioned : false
  const errors = useMemo(() => validateSettings(draft?.settings || {}, { firstSave }), [draft, firstSave])
  const isDirty = Boolean(seed && draft && isDirtyDraft(seed, draft))
  const hasErrors = Object.keys(errors).length > 0
  const canSave = isDirty && !hasErrors

  const updateSetting = (key, value) => setDraft(d => ({ ...d, settings: { ...d.settings, [key]: value } }))
  const upsertFranja = franja =>
    setDraft(d => {
      if (franja.id == null && franja._tempKey == null) franja = { ...franja, _tempKey: `tmp-${Date.now()}`, active: true }
      const exists = d.franjas.some(f => franjaKey(f) === franjaKey(franja))
      return { ...d, franjas: exists ? d.franjas.map(f => (franjaKey(f) === franjaKey(franja) ? { ...f, ...franja } : f)) : [...d.franjas, franja] }
    })
  // Match by the same id-or-_tempKey identity as upsertFranja. Matching on f.id === franja.id
  // alone would compare undefined === undefined for every not-yet-saved franja and flip all of
  // them at once; FranjasCard only exposes this action once a franja has a real id (see its
  // `f.id != null` guard), but toggleActive stays correct if that guard ever moves.
  const toggleActive = franja => setDraft(d => ({ ...d, franjas: d.franjas.map(f => (franjaKey(f) === franjaKey(franja) ? { ...f, active: !f.active } : f)) }))

  const mutation = useMutation(body => updateReceivingSettings(warehouse_id, body), {
    onSuccess: async (data, body) => {
      setReasonOpen(false)
      setSaveError(null)
      queryClient.setQueryData(['receiving_settings', warehouse_id], {
        provisioned: data.provisioned,
        settings: data.settings,
        launch_flags: data.launch_flags,
        franjas: data.franjas,
      })
      // Reset the draft from the response directly, rather than relying solely on the
      // [seed]-keyed effect below. react-query's default structuralSharing hands back the
      // SAME `data` reference from setQueryData when the new value is deep-equal to what was
      // already cached (e.g. a save whose response doesn't visibly differ from the prior
      // snapshot) — in that case `seed` never changes identity, so that effect would never
      // fire and the form would stay "dirty" after a save that just succeeded.
      setDraft(seedDraft(data))
      queryClient.invalidateQueries(['receiving_franjas', warehouse_id])
      queryClient.invalidateQueries(['receiving_suggestions', warehouse_id])
      queryClient.invalidateQueries(['receiving_history', warehouse_id])
      setNotification({
        show: true,
        type: 'success',
        title: t(body.reason.startsWith('Revertir cambio #') ? 'receiving_config.save.reverted' : 'receiving_config.save.saved'),
      })
      const hasWarnings = (data.warnings?.overbooked_dates?.length || 0) > 0 || (data.warnings?.affected_appointments || 0) > 0
      if (hasWarnings) {
        let historyRowId = null
        let row = null
        try {
          const h = await fetchReceivingHistory(warehouse_id, { limit: 1 })
          row = h.rows[0] || null
          historyRowId = row?.id ?? null
        } catch (_) {
          // Banner still renders without Revertir: row stays null, so canRevert(null) is false
          // below. Set explicitly (not just left over from a previous save) so a fetch failure
          // on THIS save can't accidentally offer reverting to a stale, unrelated row.
        }
        setHistoryRow(row)
        setLastSaveResult({ warnings: data.warnings, historyRowId })
      } else {
        setLastSaveResult(null)
        setHistoryRow(null)
      }
    },
    onError: error => {
      const parsed = parseReceivingError(error)
      setSaveError(parsed)
      // The flat 400 invalid_param stays inside the still-open reason modal (surfaced via
      // SaveReasonModal's serverError prop below); every other code closes it so the alert
      // or dialog rendered from saveError is actually visible instead of stacking under it.
      if (parsed.code !== 'invalid_param') setReasonOpen(false)
    },
  })

  const save = reason => mutation.mutate(toPutBody(seed, draft, reason))

  // Memoised because FranjaFormModal lists this array in its own overlap useMemo's deps: a
  // fresh array identity every render would defeat that memo and re-run overlappingFranja on
  // every keystroke anywhere in this container.
  const activeOthers = useMemo(
    () => (draft?.franjas || []).filter(f => f.active && (franjaModal.initial ? franjaKey(f) !== franjaKey(franjaModal.initial) : true)),
    [draft, franjaModal.initial],
  )

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <ReceivingHeader warehouse={warehouse} launchFlags={seed?.launch_flags} />
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <ReceivingTabs />
      <Prompt when={isDirty} message={t('receiving_config.save.unsaved_prompt')} />
      <Notification
        show={notification.show}
        setShow={val => setNotification(prev => ({ ...prev, show: val }))}
        type={notification.type}
        title={notification.title}
        ephemeral={false}
      />

      <div className="p-4 space-y-4 max-w-5xl">
        {settingsQuery.isError ? (
          <div className="text-center py-12">
            <p className="text-red-600 font-medium mb-3">{t('receiving_config.load_error')}</p>
            <Button type="secondary" onClick={() => settingsQuery.refetch()}>
              {t('receiving_config.retry')}
            </Button>
          </div>
        ) : !draft ? (
          <Loader show={true} />
        ) : (
          <>
            {firstSave && (
              <div className="rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm px-4 py-3">{t('receiving_config.unprovisioned_banner')}</div>
            )}
            {lastSaveResult && (
              <PostSaveWarningsBanner
                warnings={lastSaveResult.warnings}
                canRevert={canRevert(historyRow)}
                onRevert={() => setRevertOpen(true)}
                onDismiss={() => setLastSaveResult(null)}
                warehouseId={warehouse_id}
              />
            )}
            {saveError && !CODES_HANDLED_ELSEWHERE.includes(saveError.code) && (
              <div className="rounded-md bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 flex justify-between">
                <span>
                  {saveError.code === 'invalid_config' && t('receiving_config.errors.invalid_config', { message: saveError.message })}
                  {saveError.code === 'settings_required_for_first_save' &&
                    t('receiving_config.errors.settings_required_for_first_save', {
                      fields: (saveError.context.missing || []).map(k => t(`receiving_config.settings.fields.${k}`)).join(', '),
                    })}
                  {saveError.code !== 'invalid_config' && saveError.code !== 'settings_required_for_first_save' && t('receiving_config.errors.generic')}
                </span>
                <button type="button" className="text-red-700 hover:underline" onClick={() => setSaveError(null)}>
                  {t('receiving_config.warnings.dismiss')}
                </button>
              </div>
            )}
            <SettingsCard
              values={draft.settings}
              onChange={updateSetting}
              suggestions={suggestions || {}}
              requiredKeys={firstSave ? FIRST_SAVE_KEYS : []}
              errors={errors}
            />
            <FranjasCard
              franjas={draft.franjas}
              onAdd={() => setFranjaModal({ open: true, initial: null })}
              onEdit={f => setFranjaModal({ open: true, initial: f })}
              onToggleActive={toggleActive}
            />
            <div className="flex justify-end gap-2">
              <Button type="secondary" disabled={!isDirty} onClick={() => setDraft(seedDraft(seed))}>
                {t('receiving_config.save.discard')}
              </Button>
              <Button
                type="primary"
                disabled={!canSave}
                onClick={() => {
                  setSaveError(null)
                  setReasonOpen(true)
                }}
              >
                {t('receiving_config.save.button')}
              </Button>
            </div>
          </>
        )}
      </div>

      <FranjaFormModal
        open={franjaModal.open}
        setOpen={open => setFranjaModal(m => ({ ...m, open }))}
        initial={franjaModal.initial}
        otherActiveFranjas={activeOthers}
        onSubmit={upsertFranja}
      />
      <SaveReasonModal
        open={reasonOpen}
        setOpen={setReasonOpen}
        onConfirm={save}
        isLoading={mutation.isLoading}
        serverError={saveError && saveError.code === 'invalid_param' ? t('receiving_config.errors.invalid_param', { message: saveError.message }) : null}
      />
      <ConflictsDialog
        open={saveError?.code === 'franja_has_booked_appointments'}
        setOpen={() => setSaveError(null)}
        conflicts={saveError?.context?.conflicts || []}
        warehouseId={warehouse_id}
      />
      <ConfirmDialog
        open={saveError?.code === 'concurrent_update'}
        setOpen={() => setSaveError(null)}
        title={t('receiving_config.errors.concurrent_update_title')}
        description={t('receiving_config.errors.concurrent_update_description')}
        confirmLabel={t('receiving_config.errors.reload')}
        hideCancel={true}
        onConfirm={async () => {
          setSaveError(null)
          // Re-seed explicitly from the refetch result instead of relying solely on the
          // [seed]-keyed effect above. The effect only re-fires when `settingsQuery.data`
          // changes REFERENCE, but react-query's structuralSharing (and, more simply, a test
          // double that resolves the same object both times) can hand back the very same
          // reference across a refetch — the same hazard already documented on the
          // save-success path in this file's mutation.onSuccess. Without this, "Recargar"
          // could refetch the now-current server config yet leave the stale, conflicting
          // draft in place.
          const result = await settingsQuery.refetch()
          if (result.data) setDraft(seedDraft(result.data))
        }}
      />
      <ConfirmDialog
        open={revertOpen}
        setOpen={setRevertOpen}
        title={t('receiving_config.warnings.revert_confirm_title')}
        description={t('receiving_config.warnings.revert_confirm_description', { id: historyRow?.id })}
        confirmLabel={t('receiving_config.warnings.revert')}
        cancelLabel={t('receiving_config.save.modal.cancel')}
        loading={mutation.isLoading}
        type="danger"
        onConfirm={() => {
          // buildRevertBody is the ONLY thing allowed to construct this PUT body (never
          // hand-rolled here). It returns null when canRevert(historyRow) is false, but the
          // banner already hides Revertir in that case, so body should always be non-null on
          // this path — the guard is just defensive.
          const body = buildRevertBody(historyRow)
          setRevertOpen(false)
          if (body) mutation.mutate(body)
        }}
      />
    </PageView>
  )
}
